import {
  Eye,
  KeyRound,
  ShieldCheck,
  Trash2,
  UserPlus,
  UsersRound,
} from "lucide-react";
import { type FormEvent, useCallback, useState } from "react";
import { api } from "../../api/client.js";
import type { TenantRole, ThemePreference } from "../../api/types.js";
import {
  EmptyState,
  InlineError,
  LoadingTable,
} from "../../components/AsyncState.js";
import { useApiResource } from "../../hooks/useApiResource.js";
import { AppearancePanel } from "../settings/AppearancePanel.js";

const ROLES: readonly {
  value: TenantRole;
  label: string;
  description: string;
}[] = [
  {
    value: "owner",
    label: "Owner",
    description: "Full tenant administration and final approval.",
  },
  {
    value: "risk_manager",
    label: "Risk manager",
    description:
      "Can accept, refute, suppress, and approve remediation; cannot administer users.",
  },
  {
    value: "operator",
    label: "Operator",
    description: "Runs scans and prepares remediation proposals.",
  },
  {
    value: "reviewer",
    label: "Reviewer",
    description:
      "Reviews evidence and verifies proposed fixes; cannot finalize dispositions.",
  },
  { value: "viewer", label: "Viewer", description: "Read-only access." },
];

function principalDisplayName(principalId: string): string {
  if (principalId === "operator") return "operator.invalid";
  return principalId;
}

export function AdminPage({
  theme,
  onThemeChange,
}: {
  theme: ThemePreference;
  onThemeChange: (theme: ThemePreference) => void;
}) {
  const load = useCallback(
    (signal: AbortSignal) => api.listTenantMemberships(signal),
    [],
  );
  const memberships = useApiResource(load);
  const [principalId, setPrincipalId] = useState("");
  const [role, setRole] = useState<TenantRole>("viewer");
  const [pending, setPending] = useState("");
  const [message, setMessage] = useState("");

  const create = async (event: FormEvent) => {
    event.preventDefault();
    setPending("create");
    setMessage("");
    try {
      await api.createTenantMembership(principalId.trim().toLowerCase(), role);
      setPrincipalId("");
      setRole("viewer");
      setMessage("User created with the selected tenant role.");
      memberships.refresh();
    } catch (error) {
      setMessage(
        error instanceof Error
          ? error.message
          : "The user could not be created.",
      );
    } finally {
      setPending("");
    }
  };

  const changeRole = async (memberPrincipal: string, nextRole: TenantRole) => {
    setPending(memberPrincipal);
    setMessage("");
    try {
      await api.updateTenantMembership(memberPrincipal, nextRole);
      setMessage(
        `Updated ${memberPrincipal} to ${ROLES.find((entry) => entry.value === nextRole)?.label}.`,
      );
      memberships.refresh();
    } catch (error) {
      setMessage(
        error instanceof Error
          ? error.message
          : "The role could not be updated.",
      );
    } finally {
      setPending("");
    }
  };

  const remove = async (memberPrincipal: string) => {
    if (!window.confirm(`Remove ${memberPrincipal} from this tenant?`)) return;
    setPending(memberPrincipal);
    setMessage("");
    try {
      await api.deleteTenantMembership(memberPrincipal);
      setMessage(`Removed ${memberPrincipal}.`);
      memberships.refresh();
    } catch (error) {
      setMessage(
        error instanceof Error
          ? error.message
          : "The user could not be removed.",
      );
    } finally {
      setPending("");
    }
  };

  return (
    <section className="page admin-page" aria-labelledby="settings-heading">
      <header className="page-heading">
        <div>
          <span className="eyebrow">Settings</span>
          <h1 id="settings-heading">Users and approval roles</h1>
          <p>
            Assign least-privilege roles. Only an owner or risk manager can
            finalize finding acceptance, suppression, or remediation approval.
          </p>
        </div>
      </header>

      <AppearancePanel theme={theme} onThemeChange={onThemeChange} />

      <section
        className="admin-decision-hero"
        aria-label="Access management guidance"
      >
        <div>
          <span className="page-kicker">
            Control access without slowing review
          </span>
          <h2>
            Give every person the smallest role that lets them move work
            forward.
          </h2>
          <p>
            Roles make accountability visible: reviewers inspect evidence,
            operators prepare work, and only designated approvers finalize risk
            decisions.
          </p>
        </div>
        <dl>
          <div>
            <dt>
              <Eye aria-hidden="true" size={15} /> Review
            </dt>
            <dd>Inspect evidence and verify proposed fixes.</dd>
          </div>
          <div>
            <dt>
              <UsersRound aria-hidden="true" size={15} /> Operate
            </dt>
            <dd>Run scans and prepare governed remediation.</dd>
          </div>
          <div>
            <dt>
              <KeyRound aria-hidden="true" size={15} /> Approve
            </dt>
            <dd>Finalize only the decisions you own.</dd>
          </div>
        </dl>
      </section>

      <section className="panel admin-create-user">
        <div className="panel-heading">
          <UserPlus aria-hidden="true" size={18} />
          <h2>Create user</h2>
        </div>
        <div className="role-palette">
          {ROLES.map((entry) => (
            <button
              className={
                role === entry.value
                  ? "role-option role-option--selected"
                  : "role-option"
              }
              type="button"
              key={entry.value}
              onClick={() => setRole(entry.value)}
              aria-pressed={role === entry.value}
            >
              <strong>{entry.label}</strong>
              <span>{entry.description}</span>
            </button>
          ))}
        </div>
        <form
          className="admin-create-user__form"
          onSubmit={(event) => void create(event)}
        >
          <label className="form-field">
            <span>Email or principal ID</span>
            <input
              required
              maxLength={320}
              value={principalId}
              onChange={(event) => setPrincipalId(event.target.value)}
              placeholder="analyst@example.com"
            />
          </label>
          <label className="form-field">
            <span>Role</span>
            <select
              value={role}
              onChange={(event) => setRole(event.target.value as TenantRole)}
            >
              {ROLES.map((entry) => (
                <option key={entry.value} value={entry.value}>
                  {entry.label}
                </option>
              ))}
            </select>
          </label>
          <p className="role-description">
            {ROLES.find((entry) => entry.value === role)?.description}
          </p>
          <button
            className="button button--primary"
            type="submit"
            disabled={pending === "create"}
          >
            {pending === "create" ? "Creating…" : "Create user"}
          </button>
        </form>
      </section>

      {memberships.isLoading && !memberships.data ? (
        <LoadingTable columns={3} rows={4} />
      ) : null}
      {memberships.error && !memberships.data ? (
        <InlineError error={memberships.error} onRetry={memberships.refresh} />
      ) : null}
      {memberships.data && memberships.data.items.length > 0 ? (
        <section className="panel">
          <div className="panel-heading">
            <ShieldCheck aria-hidden="true" size={18} />
            <h2>Tenant members</h2>
          </div>
          <div className="table-scroll">
            <table className="data-table">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Role</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {memberships.data.items.map((member) => (
                  <tr key={member.principalId}>
                    <td>
                      <strong>
                        {principalDisplayName(member.principalId)}
                      </strong>
                    </td>
                    <td>
                      <select
                        aria-label={`Role for ${member.principalId}`}
                        value={member.role}
                        disabled={pending === member.principalId}
                        onChange={(event) =>
                          void changeRole(
                            member.principalId,
                            event.target.value as TenantRole,
                          )
                        }
                      >
                        {ROLES.map((entry) => (
                          <option key={entry.value} value={entry.value}>
                            {entry.label}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <button
                        className="icon-button"
                        type="button"
                        aria-label={`Remove ${member.principalId}`}
                        disabled={pending === member.principalId}
                        onClick={() => void remove(member.principalId)}
                      >
                        <Trash2 aria-hidden="true" size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}
      {memberships.data && memberships.data.items.length === 0 ? (
        <EmptyState
          title="No additional members yet"
          description="Create the first reviewer or operator above. Their access can be refined later, and every role change is recorded."
        />
      ) : null}
      <p className="muted-copy" aria-live="polite">
        {message}
      </p>
    </section>
  );
}
